import React, { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import {
  GoalsInReviewMode_FeedbackCycleFragment,
  ObjectiveResultUpdateMode,
} from '../../__generated__/graphql';
import { useGoalsInReviewMode } from './hooks/useGoalsInReviewMode';
import type { GoalsInReviewMode } from './hooks/useGoalsInReviewMode';
import {
  resolveObjectiveResultUpdateMode,
  useCanEditGoalResult,
} from './hooks/useCanEditGoalResult';
import { useInlineGoals } from './hooks/useInlineGoals';
import {
  InlineGoalQuestion,
  GoalResultChange,
  hasAllGoalResultsSet,
} from '../../components/EmployeeFeedbackQuestionnaire/InlineGoalQuestion';
import { FeedbackCard } from '../../components/EmployeeFeedbackQuestionnaire/EmployeeFeedbackQuestion';

type FeedbackCycleWithResultMode = GoalsInReviewMode_FeedbackCycleFragment & {
  objectiveTimePeriod?: {
    Id: string;
    framework?: {
      Id: string;
      flair__Objective_Result_Update_Mode__c: ObjectiveResultUpdateMode | null;
    } | null;
  } | null;
};

type Props = {
  feedbackCycle: FeedbackCycleWithResultMode | undefined;
  employeeId: string;
  employeeName: string;
  isManager: boolean;
  isEmployee: boolean;
  readOnly: boolean;
  goalResultsToSend: GoalResultChange[];
  onChange: (change: GoalResultChange) => void;
  onAllGoalResultsSetChange: (allSet: boolean) => void;
  onGoalsModeChange: (mode: GoalsInReviewMode) => void;
};

export const EmployeeEvaluationInlineGoals: React.FC<
  React.PropsWithChildren<Props>
> = ({
  feedbackCycle,
  employeeId,
  employeeName,
  isManager,
  isEmployee,
  readOnly,
  goalResultsToSend,
  onChange,
  onAllGoalResultsSetChange,
  onGoalsModeChange,
}) => {
  const { t } = useTranslation();
  const goalsMode = useGoalsInReviewMode(feedbackCycle);

  const resultUpdateMode = resolveObjectiveResultUpdateMode(
    feedbackCycle?.objectiveTimePeriod?.framework
      ?.flair__Objective_Result_Update_Mode__c ?? null,
  );

  const canEditResult = useCanEditGoalResult({
    mode: resultUpdateMode,
    isManager,
    isEmployee,
  });

  const objectiveTimePeriodId = feedbackCycle?.objectiveTimePeriod?.Id ?? '';

  const { objectives: inlineGoals, loading } = useInlineGoals({
    employeeId,
    objectiveTimePeriodId,
    skip:
      !feedbackCycle ||
      goalsMode !== 'INLINE_AS_QUESTIONS' ||
      !objectiveTimePeriodId,
  });

  const employeeObjectives = inlineGoals.filter(
    (o): o is Extract<(typeof inlineGoals)[number], { Id: string }> =>
      'Id' in o,
  );

  const allGoalResultsSet =
    goalsMode !== 'INLINE_AS_QUESTIONS' ||
    employeeObjectives.length === 0 ||
    hasAllGoalResultsSet(employeeObjectives, goalResultsToSend);

  useEffect(() => {
    onGoalsModeChange(goalsMode);
  }, [goalsMode, onGoalsModeChange]);

  useEffect(() => {
    onAllGoalResultsSetChange(allGoalResultsSet);
  }, [allGoalResultsSet, onAllGoalResultsSetChange]);

  if (goalsMode !== 'INLINE_AS_QUESTIONS') {
    return null;
  }

  if (loading && inlineGoals.length === 0) {
    return (
      <>
        <FeedbackCard>
          <Skeleton width="60%" height={24} className="mb-1" />
          <Skeleton width="80%" height={16} />
        </FeedbackCard>
        {[1, 2, 3].map((i) => (
          <FeedbackCard key={i}>
            <Skeleton width="40%" height={20} className="mb-2" />
            <Skeleton width="100%" height={16} count={2} />
          </FeedbackCard>
        ))}
      </>
    );
  }

  if (inlineGoals.length === 0) {
    return null;
  }

  return (
    <>
      <FeedbackCard>
        <h2 className="mb-1">
          {t('performanceReview.inlineGoalQuestion.sectionTitle', {
            name: employeeName,
          })}
        </h2>
        <p className="text-muted mb-0">
          {t('performanceReview.inlineGoalQuestion.sectionDescription')}
        </p>
      </FeedbackCard>
      {inlineGoals.map(
        (objective) =>
          'Id' in objective && (
            <InlineGoalQuestion
              key={objective.Id}
              objective={objective}
              canEdit={canEditResult && !readOnly}
              onChange={onChange}
            />
          ),
      )}
    </>
  );
};
